export const SECTION_STATES = Object.freeze({
    LOADING: 'loading',
    READY: 'ready',
    EMPTY: 'empty',
    LOAD_ERROR: 'load-error',
    MUTATING: 'mutating',
    STALE: 'stale',
    REFRESHING: 'refreshing',
    REFRESH_ERROR: 'refresh-error',
})

export const SECTION_ACTIONS = Object.freeze({
    LOAD_START: 'LOAD_START',
    LOAD_SUCCESS: 'LOAD_SUCCESS',
    LOAD_FAILURE: 'LOAD_FAILURE',
    SET_DRAFT: 'SET_DRAFT',
    MUTATION_START: 'MUTATION_START',
    MUTATION_FAILURE: 'MUTATION_FAILURE',
    MUTATION_STALE: 'MUTATION_STALE',
    MUTATION_SUCCESS: 'MUTATION_SUCCESS',
    ACCEPT_STALE: 'ACCEPT_STALE',
    REFRESH_START: 'REFRESH_START',
    REFRESH_SUCCESS: 'REFRESH_SUCCESS',
    REFRESH_FAILURE: 'REFRESH_FAILURE',
    CLEAR_FEEDBACK: 'CLEAR_FEEDBACK',
})

const recordsFor = confirmedData =>
    confirmedData && Array.isArray(confirmedData.records) ? confirmedData.records : []

export const settledSectionState = confirmedData =>
    recordsFor(confirmedData).length === 0 ? SECTION_STATES.EMPTY : SECTION_STATES.READY

export const createSectionState = (confirmedData = null, draft = null) => ({
    phase: confirmedData ? settledSectionState(confirmedData) : SECTION_STATES.LOADING,
    confirmedData,
    draft,
    loadError: null,
    refreshError: null,
    mutationError: null,
    fieldErrors: {},
    successMessage: '',
    pendingMutationKey: null,
})

export const hasConfirmedSectionData = state => !!(state && state.confirmedData)

export const canSubmitSectionMutation = state =>
    !!state && (state.phase === SECTION_STATES.READY || state.phase === SECTION_STATES.EMPTY)

const errorBody = error => {
    const candidate = Array.isArray(error) ? error[0] : error
    if (candidate && candidate.response && candidate.response.data) return candidate.response.data
    if (candidate && candidate.data) return candidate.data
    return candidate
}

export const normalizeSectionError = (error, fallbackMessage = 'The operation could not be completed.') => {
    const body = errorBody(error)
    const fieldErrors = body && body.fieldErrors && typeof body.fieldErrors === 'object'
        ? body.fieldErrors
        : {}
    return {
        code: body && typeof body.code === 'string' ? body.code : 'REQUEST_FAILED',
        message: body && typeof body.message === 'string' && body.message.trim()
            ? body.message
            : fallbackMessage,
        fieldErrors,
        current: body && body.current ? body.current : null,
    }
}

const mutationFailureState = (state, error) => {
    const normalized = normalizeSectionError(error)
    return {
        ...state,
        phase: settledSectionState(state.confirmedData),
        mutationError: normalized,
        fieldErrors: normalized.fieldErrors,
        pendingMutationKey: null,
        successMessage: '',
    }
}

export const sectionStateReducer = (state, action) => {
    switch (action.type) {
        case SECTION_ACTIONS.LOAD_START:
            return {
                ...state,
                phase: state.confirmedData ? SECTION_STATES.REFRESHING : SECTION_STATES.LOADING,
                loadError: null,
                refreshError: null,
            }
        case SECTION_ACTIONS.LOAD_SUCCESS:
            return {
                ...state,
                phase: settledSectionState(action.data),
                confirmedData: action.data,
                loadError: null,
                refreshError: null,
            }
        case SECTION_ACTIONS.LOAD_FAILURE: {
            const normalized = normalizeSectionError(action.error, 'This section could not be loaded.')
            return state.confirmedData
                ? { ...state, phase: SECTION_STATES.REFRESH_ERROR, refreshError: normalized }
                : { ...state, phase: SECTION_STATES.LOAD_ERROR, loadError: normalized }
        }
        case SECTION_ACTIONS.SET_DRAFT:
            return { ...state, draft: action.draft }
        case SECTION_ACTIONS.MUTATION_START:
            if (!canSubmitSectionMutation(state)) return state
            return {
                ...state,
                phase: SECTION_STATES.MUTATING,
                draft: action.draft === undefined ? state.draft : action.draft,
                mutationError: null,
                fieldErrors: {},
                successMessage: '',
                pendingMutationKey: action.mutationKey || 'section',
            }
        case SECTION_ACTIONS.MUTATION_FAILURE:
            return mutationFailureState(state, action.error)
        case SECTION_ACTIONS.MUTATION_STALE: {
            const normalized = normalizeSectionError(action.error, 'This section changed. Review the current values.')
            return {
                ...state,
                phase: SECTION_STATES.STALE,
                confirmedData: action.current || normalized.current || state.confirmedData,
                mutationError: normalized,
                fieldErrors: normalized.fieldErrors,
                pendingMutationKey: null,
                successMessage: '',
            }
        }
        case SECTION_ACTIONS.MUTATION_SUCCESS:
            return {
                ...state,
                phase: SECTION_STATES.REFRESHING,
                confirmedData: action.data,
                draft: action.retainDraft ? state.draft : null,
                mutationError: null,
                fieldErrors: {},
                refreshError: null,
                pendingMutationKey: action.mutationKey || state.pendingMutationKey,
                successMessage: action.successMessage || 'Changes saved.',
            }
        case SECTION_ACTIONS.ACCEPT_STALE:
            return {
                ...state,
                phase: settledSectionState(state.confirmedData),
                mutationError: null,
                fieldErrors: {},
            }
        case SECTION_ACTIONS.REFRESH_START:
            return {
                ...state,
                phase: SECTION_STATES.REFRESHING,
                refreshError: null,
            }
        case SECTION_ACTIONS.REFRESH_SUCCESS:
            return {
                ...state,
                phase: settledSectionState(action.data),
                confirmedData: action.data,
                loadError: null,
                refreshError: null,
                pendingMutationKey: null,
            }
        case SECTION_ACTIONS.REFRESH_FAILURE:
            return {
                ...state,
                phase: SECTION_STATES.REFRESH_ERROR,
                refreshError: normalizeSectionError(action.error, 'The latest values could not be loaded.'),
                pendingMutationKey: null,
            }
        case SECTION_ACTIONS.CLEAR_FEEDBACK:
            return {
                ...state,
                mutationError: null,
                fieldErrors: {},
                successMessage: '',
            }
        default:
            throw new Error(`Unhandled section state action: ${action.type}`)
    }
}
